import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  ShieldAlert,
  AlertTriangle,
  Info,
  CheckCircle2,
  Globe,
  Zap,
  Eye,
  Monitor,
  Smartphone,
  AlertCircle,
} from "lucide-react";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";

const SEVERITY_META = {
  critical: { color: "#DC2626", bg: "#FEF2F2", label: "Critical" },
  warning:  { color: "#F59E0B", bg: "#FFFBEB", label: "Warning" },
  info:     { color: "#2563EB", bg: "#EFF6FF", label: "Info" },
};

const ISSUE_TYPE_LABELS = {
  broken_link: "Broken Link",
  redirect_chain: "Redirect Chain",
  noindex: "Noindex Tag",
  canonical_conflict: "Canonical Conflict",
  missing_canonical: "Missing Canonical",
  duplicate_title: "Duplicate Title",
  missing_meta_desc: "Missing Meta Description",
  orphan_page: "Orphan Page",
  deep_click_depth: "Deep Click Depth",
  oversized_page: "Oversized Page",
  missing_h1: "Missing H1",
  no_schema: "No Structured Data",
  soft_404: "Soft 404",
  google_fetch_error: "Google Fetch Error",
  not_indexed: "Not Indexed",
  google_canonical_override: "Google Canonical Override",
  robots_blocked_by_google: "Blocked by Robots",
  mobile_usability_issue: "Mobile Usability Issue",
  rich_result_error: "Rich Result Error",
  not_in_sitemap: "Not in Sitemap",
  not_mobile_first: "Not Mobile-First",
  stale_google_crawl: "Stale Google Crawl",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreColor(score) {
  if (score === null || score === undefined) return "#9CA3AF";
  if (score >= 90) return "#22C55E";
  if (score >= 50) return "#F59E0B";
  return "#EF4444";
}

function ScoreRing({ score, label }) {
  const radius = 28;
  const circ = 2 * Math.PI * radius;
  const offset = score != null ? circ - (score / 100) * circ : circ;
  const color = scoreColor(score);

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-16 h-16">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 72 72">
          <circle cx="36" cy="36" r={radius} stroke="#E5E7EB" strokeWidth="6" fill="none" />
          <circle
            cx="36" cy="36"
            r={radius}
            stroke={color}
            strokeWidth="6"
            fill="none"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold" style={{ color }}>
            {score ?? "—"}
          </span>
        </div>
      </div>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold text-center">
        {label}
      </span>
    </div>
  );
}

function MetricRow({ label, value, score }) {
  const dot = score != null
    ? score >= 0.9 ? "#22C55E" : score >= 0.5 ? "#F59E0B" : "#EF4444"
    : "#9CA3AF";
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dot }} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <span className="text-xs font-semibold">{value ?? "—"}</span>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b bg-muted/20">
        {Icon && <Icon size={13} className="text-[#018D4C]" />}
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

// ── PSI Section ───────────────────────────────────────────────────────────────

function PsiSection({ pageUrl }) {
  const [strategy, setStrategy] = useState("mobile");
  // Cache results per strategy so tab switch never re-fetches
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noKey, setNoKey] = useState(false);

  const data = cache[strategy] ?? null;

  const run = useCallback(async (strat, forceRefresh = false) => {
    if (!forceRefresh && cache[strat]) return;
    setLoading(true);
    setError(null);
    setNoKey(false);
    try {
      const res = await api.getPsiData(pageUrl, strat, forceRefresh);
      setCache(prev => ({ ...prev, [strat]: res }));
    } catch (e) {
      const status = e?.response?.status;
      console.error("[PSI] error status=%s", status, e?.response?.data);
      if (status === 503) {
        setNoKey(true);
      } else {
        setError("PSI request failed.");
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageUrl]);

  const toggle = (strat) => {
    setStrategy(strat);
    run(strat);
  };

  useEffect(() => { run("mobile"); }, [run]);

  return (
    <SectionCard title="PageSpeed Insights" icon={Zap}>
      <div className="flex items-center gap-2 mb-4">
        {["mobile", "desktop"].map((s) => (
          <button
            key={s}
            onClick={() => toggle(s)}
            disabled={loading}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors disabled:opacity-50 ${
              strategy === s
                ? "text-white"
                : "text-muted-foreground hover:bg-muted"
            }`}
            style={strategy === s ? { background: PRIMARY } : {}}
          >
            {s === "mobile" ? <Smartphone size={12} /> : <Monitor size={12} />}
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        {!loading && (
          <button
            onClick={() => run(strategy, true)}
            className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground px-2 py-1 rounded border hover:bg-muted transition-colors"
          >
            {data?.cached ? `Cached · Refresh` : "Refresh"}
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10 text-muted-foreground gap-2 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Running Lighthouse audit…
        </div>
      )}

      {noKey && !loading && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">PSI API key not configured</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Set <code className="font-mono bg-amber-100 px-1 rounded">PAGESPEED_API_KEY</code> in
              your backend environment to enable Core Web Vitals data.
            </p>
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="text-sm text-red-600 py-2">{error}</div>
      )}

      {data && !loading && (
        <div className="space-y-5">
          {/* Score rings */}
          <div className="grid grid-cols-4 gap-3">
            <ScoreRing score={data.scores.performance} label="Performance" />
            <ScoreRing score={data.scores.accessibility} label="Accessibility" />
            <ScoreRing score={data.scores.best_practices} label="Best Practices" />
            <ScoreRing score={data.scores.seo} label="SEO" />
          </div>

          {/* Core metrics */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              Core Web Vitals
            </p>
            <MetricRow label="Largest Contentful Paint (LCP)" value={data.metrics.lcp?.displayValue} score={data.metrics.lcp?.score} />
            <MetricRow label="First Contentful Paint (FCP)" value={data.metrics.fcp?.displayValue} score={data.metrics.fcp?.score} />
            <MetricRow label="Total Blocking Time (TBT)" value={data.metrics.tbt?.displayValue} score={data.metrics.tbt?.score} />
            <MetricRow label="Cumulative Layout Shift (CLS)" value={data.metrics.cls?.displayValue} score={data.metrics.cls?.score} />
            <MetricRow label="Speed Index" value={data.metrics.speed_index?.displayValue} score={data.metrics.speed_index?.score} />
          </div>
          {data.fetched_at && (
            <p className="text-[10px] text-muted-foreground">
              {data.cached ? "Cached" : "Fetched"} {new Date(data.fetched_at).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </SectionCard>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AuditPageDetailPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.getAuditPageDetail(Number(pageId));
        if (mounted) setData(res);
      } catch {
        // no-op
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [pageId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
        <Loader2 size={18} className="animate-spin" />
        Loading page detail…
      </div>
    );
  }

  if (!data?.page) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => navigate("/dashboard/audit")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft size={14} /> Back to Audit
        </button>
        <p className="text-sm text-muted-foreground">Page not found.</p>
      </div>
    );
  }

  const { page, issues = [], incoming_links = [], outgoing_links = [] } = data;

  return (
    <div className="space-y-5 p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate("/dashboard/audit")}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
        >
          <ArrowLeft size={13} /> Back to Audit
        </button>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-lg font-bold tracking-tight truncate">
              {page.title || page.url}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs font-mono text-muted-foreground truncate">{page.url}</span>
              <a href={page.url} target="_blank" rel="noreferrer" className="flex-shrink-0">
                <ExternalLink size={12} className="text-muted-foreground hover:text-foreground" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Status", page.status_code ?? "—"],
          ["Click Depth", page.click_depth ?? "—"],
          ["Size", page.page_size_bytes ? `${(page.page_size_bytes / 1024).toFixed(0)} KB` : "—"],
          ["Indexable", page.is_indexable ? "Yes" : "No"],
        ].map(([label, val]) => (
          <div key={label} className="bg-white border rounded-xl p-4 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
            <div className="text-xl font-bold mt-1">{val}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* SEO Fields */}
        <SectionCard title="SEO Fields" icon={Globe}>
          <div className="space-y-2 text-sm">
            {[
              ["Title", page.title],
              ["H1", page.h1],
              ["Canonical", page.canonical],
              ["Meta Robots", page.meta_robots],
              ["Schema Types", page.schema_types?.join(", ")],
              ["Meta Description", page.meta_description],
            ].map(([label, val]) => val ? (
              <div key={label}>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
                <p className="text-xs mt-0.5 break-words">{val}</p>
              </div>
            ) : null)}
          </div>
        </SectionCard>

        {/* Google's View */}
        <SectionCard title="Google's View" icon={Eye}>
          {page.gsc_verdict ? (
            <div className="space-y-2 text-sm">
              {[
                ["Verdict", page.gsc_verdict],
                ["Coverage State", page.gsc_coverage_state],
                ["Fetch State", page.gsc_page_fetch_state],
                ["Google Canonical", page.gsc_google_canonical],
                ["Crawled As", page.gsc_crawled_as],
                ["Mobile", page.gsc_mobile_verdict],
                ["Rich Results", page.gsc_rich_result_verdict],
                ["Last Crawled", page.gsc_last_crawl_time ? new Date(page.gsc_last_crawl_time).toLocaleDateString() : null],
              ].map(([label, val]) => val ? (
                <div key={label}>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
                  <p className="text-xs mt-0.5 font-medium">{val}</p>
                </div>
              ) : null)}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground py-2">
              No GSC data — run enrichment from the audit page.
            </p>
          )}
        </SectionCard>
      </div>

      {/* PSI */}
      <PsiSection pageUrl={page.url} />

      {/* Issues on this page */}
      {issues.length > 0 && (
        <SectionCard title={`Issues (${issues.length})`} icon={ShieldAlert}>
          <div className="divide-y">
            {issues.map((issue) => {
              const sm = SEVERITY_META[issue.severity] || SEVERITY_META.info;
              return (
                <div key={issue.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider flex-shrink-0 mt-0.5"
                    style={{ background: sm.bg, color: sm.color }}
                  >
                    {issue.severity === "critical" && <ShieldAlert size={9} />}
                    {issue.severity === "warning" && <AlertTriangle size={9} />}
                    {issue.severity === "info" && <Info size={9} />}
                    {sm.label}
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold">{ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{issue.description}</p>
                    {issue.ai_explanation && (
                      <p className="text-xs italic text-foreground/70 mt-1">{issue.ai_explanation}</p>
                    )}
                  </div>
                  {!issue.resolved_at && (
                    <span className="ml-auto flex-shrink-0">
                      <CheckCircle2 size={14} className="text-muted-foreground/40" />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Links */}
      {(incoming_links.length > 0 || outgoing_links.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {incoming_links.length > 0 && (
            <SectionCard title={`Incoming Links (${incoming_links.length})`} icon={Globe}>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {incoming_links.map((l, i) => (
                  <div key={i} className="text-xs font-mono text-muted-foreground truncate py-0.5">
                    {l.source_url}
                    {l.anchor_text && <span className="text-foreground/60 ml-1">"{l.anchor_text}"</span>}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
          {outgoing_links.length > 0 && (
            <SectionCard title={`Outgoing Links (${outgoing_links.length})`} icon={Globe}>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {outgoing_links.map((l, i) => (
                  <div key={i} className="text-xs font-mono text-muted-foreground truncate py-0.5">
                    {l.target_url}
                    {l.anchor_text && <span className="text-foreground/60 ml-1">"{l.anchor_text}"</span>}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      )}
    </div>
  );
}
